import { useState, useCallback, useRef, useEffect } from "react";
import { AccountTreeGroup } from "../../domain/entities/AccountTreeEntity";
import { fetchManagementAccountTree } from "../../data/api/managementAccountTreeApi";
import { mergeTreeGroups } from "../../utils/glTreeUtils";
import { GlTreePickerHookState } from "../types/glTreePickerTypes";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface UseGlParentAccountTreeOptions {
  accountType: string;
  selectedAccountId?: string;
  excludeAccountId?: string;
  enabled?: boolean;
}

export function useGlParentAccountTree({
  accountType,
  selectedAccountId,
  excludeAccountId,
  enabled = true,
}: UseGlParentAccountTreeOptions): GlTreePickerHookState {
  const [groups, setGroups] = useState<AccountTreeGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingTypes] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTermState] = useState("");
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);
  const debouncedSearchRef = useRef("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingMoreRef = useRef(false);

  const searchActive = searchTerm.trim().length > 0;
  const accountTypeReady = accountType.trim() !== "";

  const fetchBrowse = useCallback(async (): Promise<void> => {
    if (!enabled || !accountTypeReady) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchManagementAccountTree({
        accountType,
        selectedAccountId,
        excludeAccountId,
      });

      if (!result.success) {
        throw new Error("Failed to fetch parent accounts");
      }

      setGroups(result.data.groups ?? []);
      setHasMore(false);
      offsetRef.current = 0;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load parent accounts";
      setError(message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [
    accountType,
    accountTypeReady,
    enabled,
    excludeAccountId,
    selectedAccountId,
  ]);

  const fetchSearchPage = useCallback(
    async (search: string, offset: number, append: boolean): Promise<void> => {
      if (!enabled || !accountTypeReady) return;

      if (append) {
        setLoadingMore(true);
        isLoadingMoreRef.current = true;
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await fetchManagementAccountTree({
          accountType,
          search,
          limit: PAGE_SIZE,
          offset,
          excludeAccountId,
        });

        if (!result.success) {
          throw new Error("Failed to fetch parent accounts");
        }

        setGroups((prev) =>
          append
            ? mergeTreeGroups(prev, result.data.groups ?? [])
            : (result.data.groups ?? []),
        );

        const nextHasMore = result.data.pagination?.has_more ?? false;
        setHasMore(nextHasMore);
        offsetRef.current = offset;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to load parent accounts";
        setError(message);
        if (!append) {
          setGroups([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    },
    [accountType, accountTypeReady, enabled, excludeAccountId],
  );

  useEffect(() => {
    if (!enabled || !accountTypeReady) {
      setGroups([]);
      setHasMore(false);
      setError(null);
      return;
    }

    offsetRef.current = 0;
    debouncedSearchRef.current = "";
    setSearchTermState("");

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    fetchBrowse();
  }, [
    accountType,
    accountTypeReady,
    enabled,
    excludeAccountId,
    selectedAccountId,
    fetchBrowse,
  ]);

  const setSearchTerm = useCallback(
    (term: string) => {
      setSearchTermState(term);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const trimmed = term.trim();
        debouncedSearchRef.current = trimmed;
        offsetRef.current = 0;

        if (trimmed) {
          fetchSearchPage(trimmed, 0, false);
        } else {
          setHasMore(false);
          fetchBrowse();
        }
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchBrowse, fetchSearchPage],
  );

  const loadMore = useCallback(() => {
    if (
      !searchActive ||
      !hasMore ||
      loading ||
      loadingMore ||
      isLoadingMoreRef.current
    ) {
      return;
    }
    const nextOffset = offsetRef.current + PAGE_SIZE;
    fetchSearchPage(debouncedSearchRef.current, nextOffset, true);
  }, [fetchSearchPage, hasMore, loading, loadingMore, searchActive]);

  const loadAccountType = useCallback(async (_type: string): Promise<void> => {
    // Single account type — tree is loaded on open.
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    groups,
    loading,
    loadingMore,
    loadingTypes,
    hasMore,
    searchTerm,
    searchActive,
    selectedAccountType: accountTypeReady ? accountType : null,
    setSearchTerm,
    loadAccountType,
    loadMore,
    error,
  };
}
